import { format, parseISO } from 'date-fns'

import type { ColumnDef } from '@/components/ui/table/types'
import type {
  CheckBrief,
  CheckSequenceGap,
  CheckTimelineRow,
  PayeeConcentrationRow,
} from '@/modules/projects/stores/types'
import { amountTone, numberToCurrency } from '@/utils'

export type PayeeRow = PayeeConcentrationRow & { id: string }
export type SequenceGapRow = CheckSequenceGap & { id: string }
export type TimelineRow = CheckTimelineRow & { id: string }

function outflowCell(value: number) {
  if (value <= 0) return <span className="text-muted-foreground">--</span>
  return <span className="text-red-600">{numberToCurrency(value)}</span>
}

function inflowCell(value: number) {
  if (value <= 0) return <span className="text-muted-foreground">--</span>
  return <span className="text-emerald-600">{numberToCurrency(value)}</span>
}

function netCell(value: number) {
  return (
    <span className={amountTone(value)}>
      {numberToCurrency(Math.abs(value))}
    </span>
  )
}

function formatCheckDate(date: string | null) {
  if (!date) return '--'
  return format(parseISO(date), 'MMM d, yyyy')
}

export function formatMissingRanges(gap: CheckSequenceGap) {
  return gap.missingRanges
    .map((range) =>
      range.from === range.to ? `${range.from}` : `${range.from}-${range.to}`,
    )
    .join(', ')
}

export const payeeColumns: ColumnDef<PayeeRow, unknown>[] = [
  {
    accessorKey: 'payee',
    header: 'Payee',
    size: 280,
    meta: { isFluid: true },
    cell: ({ row }) => (
      <span className="block truncate font-medium">{row.original.payee}</span>
    ),
  },
  { accessorKey: 'count', header: 'Checks', size: 100 },
  {
    accessorKey: 'outflow',
    header: 'Outflow',
    size: 140,
    cell: ({ row }) => outflowCell(row.original.outflow),
  },
  {
    accessorKey: 'inflow',
    header: 'Inflow',
    size: 140,
    cell: ({ row }) => inflowCell(row.original.inflow),
  },
  {
    accessorKey: 'totalAmount',
    header: 'Net',
    size: 140,
    cell: ({ row }) => netCell(row.original.totalAmount),
  },
]

export const sequenceGapColumns: ColumnDef<SequenceGapRow, unknown>[] = [
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 140,
    cell: ({ row }) => (
      <span className="text-blue-6">{row.original.accountNumber}</span>
    ),
  },
  {
    accessorKey: 'accountOwner',
    header: 'Owner',
    size: 200,
    meta: { isFluid: true },
    cell: ({ row }) => (
      <span className="block truncate">
        {row.original.accountOwner || '--'}
      </span>
    ),
  },
  {
    accessorKey: 'institution',
    header: 'Institution',
    size: 180,
    cell: ({ row }) => (
      <span className="block truncate">{row.original.institution || '--'}</span>
    ),
  },
  {
    id: 'range',
    header: 'Issued range',
    size: 140,
    cell: ({ row }) => `${row.original.from}-${row.original.to}`,
  },
  { accessorKey: 'presentCount', header: 'Present', size: 100 },
  {
    accessorKey: 'missingCount',
    header: 'Missing',
    size: 110,
    cell: ({ row }) => (
      <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700 text-xs">
        {row.original.missingCount}
      </span>
    ),
  },
  {
    id: 'gaps',
    header: 'Missing numbers',
    size: 220,
    meta: { isFluid: true },
    cell: ({ row }) => (
      <span className="block truncate text-muted-foreground">
        {formatMissingRanges(row.original)}
      </span>
    ),
  },
]

export const checkBriefColumns: ColumnDef<CheckBrief, unknown>[] = [
  {
    accessorKey: 'checkNumber',
    header: 'Check #',
    size: 110,
    cell: ({ row }) => row.original.checkNumber ?? '--',
  },
  {
    accessorKey: 'payee',
    header: 'Payee',
    size: 240,
    meta: { isFluid: true },
    cell: ({ row }) => (
      <span className="block truncate">{row.original.payee}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 140,
    cell: ({ row }) => netCell(row.original.amount),
  },
  {
    accessorKey: 'date',
    header: 'Date',
    size: 140,
    cell: ({ row }) => formatCheckDate(row.original.date),
  },
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 130,
    cell: ({ row }) => (
      <span className="text-blue-6">{row.original.accountNumber || '--'}</span>
    ),
  },
]

export const timelineColumns: ColumnDef<TimelineRow, unknown>[] = [
  {
    id: 'month',
    header: 'Month',
    size: 160,
    cell: ({ row }) =>
      format(
        new Date(row.original.year, row.original.month - 1, 1),
        'MMM yyyy',
      ),
  },
  { accessorKey: 'count', header: 'Checks', size: 100 },
  {
    accessorKey: 'outflow',
    header: 'Outflow',
    size: 140,
    cell: ({ row }) => outflowCell(row.original.outflow),
  },
  {
    accessorKey: 'inflow',
    header: 'Inflow',
    size: 140,
    cell: ({ row }) => inflowCell(row.original.inflow),
  },
  {
    accessorKey: 'totalAmount',
    header: 'Net',
    size: 140,
    cell: ({ row }) => netCell(row.original.totalAmount),
  },
]
